// Shared renderer for recent-activity rows. Used by HomePage's Recent
// Activity block and the full ActivityPage. Kept together so the two
// surfaces stay visually identical without a copy-paste drift.

function formatPlayedDate(dateVal) {
  if (!dateVal) return ''
  let s = dateVal
  if (dateVal instanceof Date) s = dateVal.toISOString().split('T')[0]
  else if (typeof dateVal === 'string' && dateVal.includes('T')) s = dateVal.split('T')[0]
  const d = new Date(s + 'T12:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
}

function initialsOf(name) {
  if (!name) return ''
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}.${parts[parts.length - 1][0]}.`.toUpperCase()
  return (parts[0] || '').slice(0, 2).toUpperCase()
}

function firstNameLastInitial(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1][0]}.`
  return parts[0] || 'Player'
}

// Trailing score block shared by EVERY activity tile so the two score columns
// line up down the whole feed:
//   Column 1 (number) — mono, right-anchored. An optional win/loss badge sits
//     to the LEFT of the number inside this same column, so badges land in the
//     number column (never a separate far-left column).
//   Column 2 (over/under) — the vs-par figure in its own fixed-width,
//     right-aligned column with the existing vspar color classes.
// Fixed min-widths keep both columns' right edges stable tile-to-tile: number
// up to "102"/"4&2", over/under up to "+30"/"-10".
//
// When a tile has NO over/under (game/badge tiles — Best Ball, Match — where
// the trailing figure is a score like "88"/"4&2"/"2&1" beside a Winner/Lost/
// Tied badge), the score number takes the OVER/UNDER (right) column slot so it
// right-aligns to the tile's right edge — the same edge over/under values sit
// on for stroke tiles — and the badge stays alone in the number (left) column.
// Stroke tiles and co-played rows (which HAVE an over/under) are unchanged.
// `outerStyle` lets a caller nudge the whole block (unused now the co-played
// box carries its own right padding).
const SCORE_NUM_MINWIDTH = '3ch'
const SCORE_OU_MINWIDTH = '3ch'
const SCORE_COL_GAP = 8
function scoreCols({ number, ouStr = '', vsClass = '', badge = null, outerStyle }) {
  const hasOu = ouStr !== '' && ouStr != null
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, fontFamily: 'var(--font-mono)', ...outerStyle }}>
      {/* Column 1 (number column) — badge always here; the score number joins
          it only when there's an over/under. With no over/under the number
          moves to Column 2 and this column holds the badge alone, so badges
          still line up in the number column across the badge tiles. */}
      <span style={{
        minWidth: SCORE_NUM_MINWIDTH,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6,
        fontSize: '0.9rem', fontWeight: 700, whiteSpace: 'nowrap',
      }}>
        {badge}
        {hasOu && <span>{number}</span>}
      </span>
      {/* Column 2 (over/under column) — the vs-par figure when present, else
          the score number right-aligned to the tile's right edge. Same mono
          weight/size the number has in Column 1; no vspar color class when it's
          a score (only the real over/under figure gets the over/under colors). */}
      <span
        className={hasOu ? `vspar ${vsClass}` : undefined}
        style={{
          minWidth: SCORE_OU_MINWIDTH, marginLeft: SCORE_COL_GAP,
          textAlign: 'right', fontSize: '0.9rem',
          fontWeight: hasOu ? 500 : 700, whiteSpace: 'nowrap',
        }}
      >{hasOu ? ouStr : number}</span>
    </div>
  )
}

export function renderActivityCard({ r, i, player, navigate }) {
  const isMe = r.player_id === player?.id
  const who = isMe ? 'You' : (r.player_name || 'Someone')
  const dateStr = formatPlayedDate(r.played_at || r.created_at)
  const rt = (r.round_type || 'stroke').toLowerCase()
  const typeName = ({
    scramble: 'Scramble',
    match: 'Match Play',
    '2v2': 'Best Ball',
  })[rt] || null
  const venue = (
    r.courses_in_club === 1 || !r.course_name || r.club_name === r.course_name
  )
    ? (r.club_name || r.course_name || 'Unknown')
    : (r.club_name ? `${r.club_name} - ${r.course_name}` : r.course_name)
  const isTeamFormat = rt === 'match' || rt === '2v2'
  const isMatch = rt === 'match'
  const vsPar = r.score_vs_par
  const ouStr = vsPar == null ? '' : (vsPar === 0 ? 'E' : vsPar > 0 ? `+${vsPar}` : String(vsPar))
  const vsClass = vsPar < 0 ? 'under' : vsPar > 0 ? 'over' : ''
  const showVsPar = !isTeamFormat && ouStr

  // Score to display:
  //   match  → match_result.label (e.g. "2&1", "1 up", "AS") — a summed
  //             stroke total ("Lost 216") is meaningless for match play
  //   2v2    → team_total (per-hole best-ball sum)
  //   other  → individual total_score
  const displayScore = isMatch
    ? (r.match_result?.label || null)
    : (isTeamFormat && r.team_total != null ? r.team_total : r.total_score)

  // Winner determination — match uses match_result.won/lost/tied; best-ball
  // still uses team_total < opponent_total.
  let winnerSide = null
  if (isMatch && r.match_result) {
    if (r.match_result.won) winnerSide = 'mine'
    else if (r.match_result.lost) winnerSide = 'theirs'
    else if (r.match_result.tied) winnerSide = 'tied'
  } else if (isTeamFormat && r.opponent_total != null && r.team_total != null) {
    if (r.team_total < r.opponent_total) winnerSide = 'mine'
    else if (r.team_total > r.opponent_total) winnerSide = 'theirs'
    else winnerSide = 'tied'
  }
  const winnerNames = winnerSide === 'mine'
    ? (r.team_members || [])
    : winnerSide === 'theirs'
      ? (r.opponent_team_members || [])
      : []
  const winnerInitials = winnerNames.map(m => initialsOf(m.name)).filter(Boolean).join(', ')

  // team_members from the server is already filtered to the caller's known
  // set (friends + caller). So knownCount = length. Multi-friend layout
  // only when the caller isn't in the round AND 2+ knowns are on the team.
  const teamMembers = Array.isArray(r.team_members) ? r.team_members : []
  const iAmInTeam = !!(player?.id && teamMembers.some(m => m.player_id === player.id))
  const isFriendTeamCard = !isMe && !iAmInTeam && teamMembers.length >= 2

  const openRound = () => {
    if (r.standalone_round_id) {
      // F-104-T5: no longer passes a `holes` count — the stale pattern
      // `r.holes_data?.length || r.holes_completed || 18` mis-passed 18 for a
      // completed back-9 round (holes_data is the course's full 18 and
      // holes_completed is a count, never the starting-hole). RoundLeaderboard
      // now fetches hole_count + starting_hole from standalone_rounds via
      // get_round_meta, so the activity-tile hand-off doesn't need to guess.
      navigate('/round-leaderboard', { state: { standalone_round_id: r.standalone_round_id, course: { name: r.course_name, par: r.course_par, id: r.course_id, holes_data: r.holes_data } } })
    } else if (r.event_id) {
      navigate('/leaderboard', { state: { event_id: r.event_id } })
    }
  }

  // Co-played solo stroke-play round: two or more friends (optionally
  // including the viewer) on the SAME physical round, collapsed by
  // groupActivityByRound into one tile that lists each participant's final
  // total score. Distinct from the team/match "competed in" card above —
  // that one shows a winner badge; this one lists individual gross scores.
  if (r.co_played && Array.isArray(r.participants) && r.participants.length > 1) {
    const parts = r.participants
    const anyMe = parts.some(pp => pp.is_me)
    const shortName = pp => (pp.is_me ? 'You' : firstNameLastInitial(pp.player_name))
    const names = parts.map(shortName)
    // Owner wording: matches the single-player "finished a round" verb.
    //   "You finished a round with Kyle P. at" (viewer leads when present)
    //   "Adam S. finished a round with Bob J. at" (non-viewer, alphabetical)
    //   3+: "You finished a round with Adam S. and Bob J. at"
    // participants[] is already ordered viewer-first-then-alphabetical (or
    // fully alphabetical without the viewer) by groupActivityByRound.
    const leadName = names[0]
    const rest = names.slice(1)
    const restList = rest.length === 1
      ? rest[0]
      : `${rest.slice(0, -1).join(', ')} and ${rest[rest.length - 1]}`
    return (
      <button
        key={'coplay-' + (r.standalone_round_id || i)}
        className="home-activity-row"
        type="button"
        onClick={openRound}
      >
        <div className="home-activity-avatar" aria-hidden="true">
          <img src={anyMe ? '/golfball.png' : '/tf-shield.webp'} alt="" />
        </div>
        <div className="home-activity-body">
          <div className="home-activity-line1">
            <div className="home-activity-line1-text">
              <span className="who">{leadName}</span>
              <span className="verb"> finished a round with </span>
              <span className="who">{restList}</span>
              <span className="verb"> at</span>
            </div>
            <div className="home-activity-scores">
              <span className="when">{dateStr}</span>
            </div>
          </div>
          <div className="home-activity-line2">
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{venue}</span>
          </div>
          {/* Bordered box around the co-played rows. The tile itself has a
              14px right inset (.home-activity-row padding-right), so a box at
              the body's content edge would sit 14px shy of the tile's right
              border. marginRight:-9 extends the box's right border ~9px out
              toward that edge (leaving a small gap off the rounded corner),
              and paddingRight:8 gives the scores breathing room inside. Net:
              9px out minus 1px border minus 8px padding = 0, so the score
              columns still land at the EXACT same right edge as the unboxed
              single-player tiles above/below — the border moves out, the
              scores don't move in. Left/top/bottom padding unchanged. */}
          <div style={{ border: '1px solid var(--green-mid)', borderRadius: 6, padding: '6px 8px', marginTop: 6, marginRight: -9 }}>
            {parts.map((pp, pi) => {
              const pVsPar = pp.score_vs_par
              const pOu = pVsPar == null ? '' : (pVsPar === 0 ? 'E' : pVsPar > 0 ? `+${pVsPar}` : String(pVsPar))
              const pVsClass = pVsPar < 0 ? 'under' : pVsPar > 0 ? 'over' : ''
              return (
                <div
                  key={(pp.player_id || pi) + '-coplay-score'}
                  className="home-activity-line2"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: pi === 0 ? 0 : 2 }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {shortName(pp)}
                  </span>
                  {scoreCols({
                    number: pp.total_score == null ? '–' : pp.total_score,
                    ouStr: pOu,
                    vsClass: pVsClass,
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </button>
    )
  }

  if (isFriendTeamCard) {
    const nameList = teamMembers.map(m => firstNameLastInitial(m.name)).join(', ')
    return (
      <button
        key={(r.round_score_id || r.player_id) + '-' + i}
        className="home-activity-row"
        type="button"
        onClick={openRound}
      >
        <div className="home-activity-avatar" aria-hidden="true">
          <img src="/tf-shield.webp" alt="" />
        </div>
        <div className="home-activity-body">
          <div className="home-activity-line1">
            <div className="home-activity-line1-text">
              <span className="who">{nameList}</span>
              <span className="verb"> competed in</span>
            </div>
            <div className="home-activity-scores">
              <span className="when">{dateStr}</span>
            </div>
          </div>
          <div className="home-activity-line2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {typeName ? `${typeName} at` : 'a round at'}
            </span>
            {winnerSide && winnerSide !== 'tied' && winnerInitials && (
              <span style={{ fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                <span style={{ color: 'var(--green-bright)', fontWeight: 700, marginRight: 6 }}>Winner</span>
                <span style={{ color: 'var(--gray-300)', fontFamily: 'var(--font-mono)' }}>{winnerInitials}</span>
              </span>
            )}
            {winnerSide === 'tied' && (
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--gray-300)' }}>Tied</span>
            )}
          </div>
          <div className="home-activity-line2" style={{ marginTop: 2 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{venue}</span>
          </div>
        </div>
      </button>
    )
  }

  const resultLabel = winnerSide === 'mine' ? 'Winner'
                    : winnerSide === 'theirs' ? 'Lost'
                    : winnerSide === 'tied' ? 'Tied' : null
  const verb = typeName ? `played ${typeName} at` : 'finished a round at'
  return (
    <button
      key={(r.round_score_id || r.player_id) + '-' + i}
      className="home-activity-row"
      type="button"
      onClick={openRound}
    >
      <div className="home-activity-avatar" aria-hidden="true">
        <img src={isMe ? '/golfball.png' : '/tf-shield.webp'} alt="" />
      </div>
      <div className="home-activity-body">
        <div className="home-activity-line1">
          <div className="home-activity-line1-text">
            <span className="who">{who}</span>
            <span className="verb"> {verb}</span>
          </div>
          <div className="home-activity-scores">
            <span className="when">{dateStr}</span>
          </div>
        </div>
        <div className="home-activity-line2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{venue}</span>
          {scoreCols({
            number: displayScore || '–',
            ouStr: showVsPar ? ouStr : '',
            vsClass: showVsPar ? vsClass : '',
            badge: resultLabel && (
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em',
                padding: '2px 8px', borderRadius: 4,
                background: resultLabel === 'Winner' ? 'rgba(74, 140, 74, 0.18)'
                          : resultLabel === 'Lost'   ? 'rgba(178, 59, 46, 0.18)'
                                                     : 'rgba(150, 150, 150, 0.15)',
                color: resultLabel === 'Winner' ? 'var(--green-bright)'
                     : resultLabel === 'Lost'   ? 'var(--red)'
                                                : 'var(--gray-300)',
              }}>{resultLabel}</span>
            ),
          })}
        </div>
      </div>
    </button>
  )
}
